import React, { useEffect, useRef, useState } from 'react';
import { array, bool, func, number, object, oneOfType, shape } from 'prop-types';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { LuHeart, LuShare2, LuStar } from 'react-icons/lu';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { trackIntercomEvent } from '../../util/intercom';
import { userDisplayNameAsString } from '../../util/data';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { Avatar, NamedLink, VerifiedUserMark } from '../../components';
import ContactButton from './ContactButton';

import css from './SectionSellerBar.css';

const LINK_COPIED_TIMEOUT_MS = 2000;

// Figma 313:1818: the freelancer row under the service title. Avatar, name,
// profile title and rating on the left; save, share and Contact on the right.
export const SectionSellerBarComponent = props => {
  const {
    author,
    reviewsSummary,
    showContactUser,
    onContactUser,
    currentUser,
    listing,
    favoriteListings,
    updateFavoriteListings,
    history,
    intl,
  } = props;

  const [linkCopied, setLinkCopied] = useState(false);
  const linkCopiedTimeout = useRef(null);
  useEffect(() => () => clearTimeout(linkCopiedTimeout.current), []);

  const authorId = author?.id?.uuid;
  const displayName = userDisplayNameAsString(author, '');
  const profileTitle = author?.attributes?.profile?.publicData?.whoAmI;
  const { average, count } = reviewsSummary || {};
  const hasRating = count > 0 && Number.isFinite(average);

  const listingId = listing?.id?.uuid;
  // Drafts and listings pending approval have no public URL to share yet.
  const listingState = listing?.attributes?.state;
  const canShare =
    listingState !== LISTING_STATE_DRAFT && listingState !== LISTING_STATE_PENDING_APPROVAL;

  // Favourite toggle, moved from the old SectionHeading.
  const isFavoriteListing = !!favoriteListings && favoriteListings.some(x => x.uuid === listingId);

  const handleAddRemoveFavorite = () => {
    if (!currentUser) {
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}));
      return;
    }
    const favoriteListingsUuids = favoriteListings
      .map(favorite => favorite.uuid)
      .filter(uuid => uuid !== listingId);

    if (isFavoriteListing) {
      updateFavoriteListings(favoriteListingsUuids);
    } else {
      if (typeof window === 'object' && window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_to_favorites',
          content_type: 'listing',
          ecommerce: {
            items: [
              {
                item_id: listingId,
                item_name: listing.attributes.title,
                quantity: '1',
                item_list_name: 'search_results',
              },
            ],
          },
        });
      }
      if (typeof window === 'object') {
        trackIntercomEvent('addedListingToFavorites', {
          listingId,
          listingTitle: listing.attributes.title,
          authorId: listing.author?.id?.uuid,
          authorName: displayName || listing.author?.attributes?.profile?.displayName,
        });
      }
      updateFavoriteListings([...favoriteListingsUuids, listingId]);
    }
  };

  const handleShare = () => {
    const path = createResourceLocatorString(
      'ListingPage',
      routeConfiguration(),
      { id: listingId, slug: createSlug(listing.attributes.title || '') },
      {}
    );
    const url = `${config.canonicalRootURL}${path}`;

    if (typeof navigator === 'undefined') {
      return;
    }
    if (typeof navigator.share === 'function') {
      // A dismissed share sheet rejects; nothing to report.
      navigator.share({ url }).catch(() => {});
    } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setLinkCopied(true);
          clearTimeout(linkCopiedTimeout.current);
          linkCopiedTimeout.current = setTimeout(
            () => setLinkCopied(false),
            LINK_COPIED_TIMEOUT_MS
          );
        })
        .catch(() => {});
    }
  };

  const saveLabel = intl.formatMessage({ id: 'SectionSellerBar.save' });
  const shareLabel = intl.formatMessage({
    id: linkCopied ? 'SectionSellerBar.linkCopied' : 'SectionSellerBar.share',
  });

  const name = authorId ? (
    <NamedLink className={css.name} name="ProfilePage" params={{ id: authorId }}>
      {displayName}
    </NamedLink>
  ) : (
    <span className={css.name}>{displayName}</span>
  );

  const rating = hasRating ? (
    <span className={css.rating}>
      <LuStar className={css.starIcon} aria-hidden="true" />
      <span className={css.ratingScore}>
        {intl.formatNumber(average, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
      <span className={css.ratingCount}>
        <FormattedMessage id="SectionSellerBar.reviewCount" values={{ count }} />
      </span>
    </span>
  ) : null;

  return (
    <div className={css.root}>
      <div className={css.freelancer}>
        <span className={css.avatarWrapper}>
          <Avatar
            rootClassName={css.avatar}
            user={author}
            renderSizes="56px"
            disableVerifiedBadge
          />
          <VerifiedUserMark user={author} rootClassName={css.verifiedMark} />
        </span>
        <div className={css.nameColumn}>
          {name}
          {profileTitle || rating ? (
            <div className={css.meta}>
              {profileTitle ? <span className={css.profileTitle}>{profileTitle}</span> : null}
              {profileTitle && rating ? (
                <span className={css.metaSeparator} aria-hidden="true">
                  ·
                </span>
              ) : null}
              {rating}
            </div>
          ) : null}
        </div>
      </div>
      {showContactUser || canShare ? (
        <div className={css.actions}>
          {showContactUser ? (
            <button
              type="button"
              className={classNames(css.iconButton, { [css.iconButtonSaved]: isFavoriteListing })}
              onClick={handleAddRemoveFavorite}
              aria-label={saveLabel}
              aria-pressed={isFavoriteListing}
              title={saveLabel}
            >
              <LuHeart className={css.icon} aria-hidden="true" />
            </button>
          ) : null}
          {canShare ? (
            <span className={css.shareWrapper}>
              <button
                type="button"
                className={css.iconButton}
                onClick={handleShare}
                aria-label={shareLabel}
                title={shareLabel}
              >
                <LuShare2 className={css.icon} aria-hidden="true" />
              </button>
              <span className={css.linkCopied} role="status" aria-live="polite">
                {linkCopied ? <FormattedMessage id="SectionSellerBar.linkCopied" /> : null}
              </span>
            </span>
          ) : null}
          {showContactUser ? (
            <ContactButton className={css.contactButton} onClick={() => onContactUser()}>
              <FormattedMessage id="SectionSellerBar.contact" />
            </ContactButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

SectionSellerBarComponent.defaultProps = {
  reviewsSummary: { average: 0, count: 0 },
  showContactUser: false,
  currentUser: null,
  favoriteListings: [],
};

SectionSellerBarComponent.propTypes = {
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  reviewsSummary: shape({ average: number, count: number }),
  showContactUser: bool,
  onContactUser: func.isRequired,
  currentUser: propTypes.currentUser,
  listing: object.isRequired,
  favoriteListings: array,
  updateFavoriteListings: func.isRequired,

  // from withRouter
  history: shape({ push: func.isRequired }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(SectionSellerBarComponent);
